"use client";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, } from "react";
import classNames from "classnames";
import { useViewport } from "@travel/shared/hooks";
import { Input } from "../input/input";
import { DropDownV2, } from "../../dropdowns/drop-down-v2/drop-down";
import { Selector } from "../selector/selector";
import ChevronIcon from "@travel/shared/assets/icons/icon-chevron-down.svg";
import styles from "./selector-drop-down.module.css";
var EMPTY_SELECTION = {};
export var SelectorDropDown = forwardRef(function SelectorDropDown(_a, ref) {
    var valueProp = _a.value, list = _a.list, _b = _a.groupPriorities, groupPriorities = _b === void 0 ? [] : _b, className = _a.className, classes = _a.classNames, 
    //@TODO: Refactor into a set of IDs
    _c = _a.selected, 
    //@TODO: Refactor into a set of IDs
    selectedProp = _c === void 0 ? EMPTY_SELECTION : _c, focusedProp = _a.focused, hideArrow = _a.hideArrow, text = _a.text, _d = _a.autoSelectClosestOnBlur, autoSelectClosestOnBlur = _d === void 0 ? true : _d, clear = _a.clear, renderTitle = _a.renderTitle, renderItem = _a.renderItem, renderInput = _a.renderInput, renderInputContent = _a.renderInputContent, renderValue = _a.renderValue, computeValue = _a.computeValue, onChange = _a.onChange, onSelect = _a.onSelect, onClickItem = _a.onClickItem, onFocusChange = _a.onFocusChange, onClickClose = _a.onClickClose, rest = __rest(_a, ["value", "list", "groupPriorities", "className", "classNames", "selected", "focused", "hideArrow", "text", "autoSelectClosestOnBlur", "clear", "renderTitle", "renderItem", "renderInput", "renderInputContent", "renderValue", "computeValue", "onChange", "onSelect", "onClickItem", "onFocusChange", "onClickClose"]);
    var _e = __read(useState(selectedProp || EMPTY_SELECTION), 2), selected = _e[0], setSelected = _e[1];
    var _f = __read(useState(valueProp), 2), value = _f[0], setValue = _f[1];
    var _g = __read(useState(), 2), focused = _g[0], setFocused = _g[1];
    var _h = __read(useState(focusedProp), 2), isFocused = _h[0], setIsFocused = _h[1];
    var groups = useMemo(function () { return computeGroups(list); }, [list]);
    var focusedIndexRef = useRef(-1);
    var selectionID = Object.keys(selected)
        .sort()
        .map(function (key) { return "".concat(key, "=").concat(selected[key]); })
        .toString();
    var isSmall = useViewport().isSmall;
    var dropDownRef = useRef(null);
    var handleKeyDown = useCallback(function (event) {
        var keyCode = event.keyCode;
        var enter = keyCode === 13;
        var up = keyCode === 38;
        var down = keyCode === 40;
        if (enter) {
            event.preventDefault();
            var selectedItem = findGroupItem(groups, focusedIndexRef.current);
            select(selectedItem);
            setIsFocused(false);
        }
        else if (up || down) {
            var nextFocusedIndex = computeFocusedIndex(keyCode, focusedIndexRef.current, list === null || list === void 0 ? void 0 : list.length);
            var focusedItem = findGroupItem(groups, nextFocusedIndex);
            focusedIndexRef.current = nextFocusedIndex;
            setFocused(focusedItem === null || focusedItem === void 0 ? void 0 : focusedItem.id);
        }
    }, [list, groups, onSelect]);
    useImperativeHandle(ref, function () {
        return __assign(__assign({}, dropDownRef === null || dropDownRef === void 0 ? void 0 : dropDownRef.current), { select: select });
    }, [dropDownRef === null || dropDownRef === void 0 ? void 0 : dropDownRef.current]);
    useEffect(function () {
        if (!isFocused)
            return;
        window.addEventListener("keydown", handleKeyDown);
        return function () {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [isFocused, handleKeyDown]);
    useEffect(function () {
        var focusedItem = list === null || list === void 0 ? void 0 : list.find(function (_a) {
            var id = _a.id;
            return id === focused;
        });
        if (focusedItem) {
            setValue((computeValue === null || computeValue === void 0 ? void 0 : computeValue(focusedItem)) ||
                focusedItem.displayName ||
                focusedItem.name ||
                focusedItem.id);
        }
    }, [focused]);
    useEffect(function () {
        setValue(valueProp);
    }, [valueProp]);
    useEffect(function () {
        setSelected(selectedProp);
    }, [selectedProp]);
    useEffect(function () {
        var focusedID = Object.keys(selected || EMPTY_SELECTION)[0];
        var focusedItem = list === null || list === void 0 ? void 0 : list.find(function (_a) {
            var id = _a.id;
            return id === focusedID;
        });
        setSelected(selected || EMPTY_SELECTION);
        setFocused(focusedID);
        if (focusedItem) {
            setValue((computeValue === null || computeValue === void 0 ? void 0 : computeValue(focusedItem)) ||
                focusedItem.displayName ||
                focusedItem.name ||
                focusedItem.id);
        }
    }, [selectionID]);
    useEffect(function () {
        setIsFocused(focusedProp);
    }, [focusedProp]);
    return (_jsx(_Fragment, { children: _jsx(DropDownV2, __assign({}, rest, { className: className, open: isFocused, trigger: function (state, ref, isClone) {
                var _a;
                var showArrow = !hideArrow && (!!text || (!isClone && !hideArrow));
                return (_jsxs("div", { className: classNames(styles["trigger"], (_a = {},
                        _a[styles["has-arrow"]] = showArrow,
                        _a[styles["has-error"]] = !!rest.error,
                        _a[styles["is-text"]] = !!text,
                        _a)), children: [(renderInput === null || renderInput === void 0 ? void 0 : renderInput(__assign(__assign({}, rest), { classNames: {
                                input: styles["input"],
                            }, value: value, horizontal: isClone ? false : rest.horizontal, transparent: isClone ? false : rest.transparent, ref: ref, onChange: handleInputChange }), state, isClone)) || (_jsx(_Fragment, { children: _jsx(Input, __assign({}, rest, { clear: clear, horizontal: isClone ? false : rest.horizontal, transparent: isClone ? false : rest.transparent, classNames: {
                                    input: styles["input"],
                                }, value: value, error: !isClone && !state.isFocused ? rest.error : undefined, forceFocused: state.isFocused, size: isSmall && isClone ? "lg" : "md", children: renderInputContent === null || renderInputContent === void 0 ? void 0 : renderInputContent(__assign(__assign({}, rest), { value: value, ref: ref, onChange: handleInputChange }), state, isClone), onChange: handleInputChange, ref: ref })) })), showArrow && (_jsx(ChevronIcon, { className: styles["arrow"], width: 18 }))] }));
            }, content: function (state) { return (_jsx("div", { className: classNames(styles["results"], classes === null || classes === void 0 ? void 0 : classes.results), children: _jsx("ul", { className: styles["groups"], children: groups === null || groups === void 0 ? void 0 : groups.map(function (group) { return (_jsx(SelectorDropDownListGroup, { itemClassName: classes === null || classes === void 0 ? void 0 : classes.item, category: group.category, items: group.items, selected: selected, focused: focused, renderTitle: renderTitle, renderItem: renderItem, onSelect: handleSelect, onClickItem: onClickItem }, group.category)); }) }) })); }, onOpenChange: handleFocusChange, ref: dropDownRef })) }));
    function select(item) {
        var _a;
        if (item === null || item === void 0 ? void 0 : item.id) {
            var value_1 = (computeValue === null || computeValue === void 0 ? void 0 : computeValue(item)) ||
                item.displayName ||
                item.name ||
                item.id;
            setSelected((_a = {},
                _a[item.id] = true,
                _a));
            setValue(value_1);
            onSelect === null || onSelect === void 0 ? void 0 : onSelect(item);
            //(onChange as any)?.(item);
        }
        else {
            if (selected !== EMPTY_SELECTION) {
                setSelected(EMPTY_SELECTION);
            }
            onSelect === null || onSelect === void 0 ? void 0 : onSelect();
        }
        focusedIndexRef.current = getItemIndex(item);
        setFocused(item === null || item === void 0 ? void 0 : item.id);
    }
    function handleInputChange(event) {
        var _a;
        select();
        setValue(event.currentTarget.value);
        setFocused((_a = list === null || list === void 0 ? void 0 : list[0]) === null || _a === void 0 ? void 0 : _a.id);
        onChange === null || onChange === void 0 ? void 0 : onChange(event);
    }
    function handleSelect(selected) {
        var selectedID = Object.keys(selected)[0];
        var selectedItem = list === null || list === void 0 ? void 0 : list.find(function (_a) {
            var id = _a.id;
            return String(id) === selectedID;
        });
        select(selectedItem);
        setIsFocused(false);
    }
    function handleFocusChange(isFocused) {
        if (!isFocused && autoSelectClosestOnBlur) {
            var normalizedValue_1 = value === null || value === void 0 ? void 0 : value.toLowerCase();
            var closest = normalizedValue_1
                ? list === null || list === void 0 ? void 0 : list.find(function (_a) {
                    var id = _a.id, name = _a.name;
                    return String(id).toLowerCase() === normalizedValue_1 ||
                        (name === null || name === void 0 ? void 0 : name.toLowerCase()) === normalizedValue_1;
                })
                : null;
            if (closest) {
                select(closest);
            }
        }
        setIsFocused(isFocused);
        onFocusChange === null || onFocusChange === void 0 ? void 0 : onFocusChange(isFocused);
    }
    function handleClickClose() {
        setFocused(undefined);
        setValue(valueProp);
        onClickClose === null || onClickClose === void 0 ? void 0 : onClickClose();
    }
    function getItemIndex(item) {
        var e_1, _a, e_2, _b;
        if (!item)
            return -1;
        var currentIndex = 0;
        try {
            for (var groups_1 = __values(groups), groups_1_1 = groups_1.next(); !groups_1_1.done; groups_1_1 = groups_1.next()) {
                var group = groups_1_1.value;
                try {
                    for (var _c = (e_2 = void 0, __values(group.items)), _d = _c.next(); !_d.done; _d = _c.next()) {
                        var id = _d.value.id;
                        if (id === item.id) {
                            return currentIndex;
                        }
                        currentIndex++;
                    }
                }
                catch (e_2_1) { e_2 = { error: e_2_1 }; }
                finally {
                    try {
                        if (_d && !_d.done && (_b = _c.return)) _b.call(_c);
                    }
                    finally { if (e_2) throw e_2.error; }
                }
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (groups_1_1 && !groups_1_1.done && (_a = groups_1.return)) _a.call(groups_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
        return -1;
    }
    function computeFocusedIndex(keyCode, current, limit) {
        if (current === void 0) { current = 0; }
        if (limit === void 0) { limit = 0; }
        var up = keyCode === 38;
        var down = keyCode === 40;
        var nextFocusedIndex = current;
        if (up || down) {
            if (up) {
                nextFocusedIndex--;
                if (nextFocusedIndex < 0) {
                    nextFocusedIndex = limit - 1;
                }
            }
            else {
                nextFocusedIndex++;
                if (nextFocusedIndex >= limit) {
                    nextFocusedIndex = 0;
                }
            }
        }
        return nextFocusedIndex;
    }
    function findGroupItem(groups, index) {
        var e_3, _a, e_4, _b;
        var item;
        var currentIndex = 0;
        try {
            for (var groups_2 = __values(groups), groups_2_1 = groups_2.next(); !groups_2_1.done; groups_2_1 = groups_2.next()) {
                var group = groups_2_1.value;
                try {
                    for (var _c = (e_4 = void 0, __values(group.items)), _d = _c.next(); !_d.done; _d = _c.next()) {
                        var item_1 = _d.value;
                        if (currentIndex === index) {
                            return item_1;
                        }
                        currentIndex++;
                    }
                }
                catch (e_4_1) { e_4 = { error: e_4_1 }; }
                finally {
                    try {
                        if (_d && !_d.done && (_b = _c.return)) _b.call(_c);
                    }
                    finally { if (e_4) throw e_4.error; }
                }
            }
        }
        catch (e_3_1) { e_3 = { error: e_3_1 }; }
        finally {
            try {
                if (groups_2_1 && !groups_2_1.done && (_a = groups_2.return)) _a.call(groups_2);
            }
            finally { if (e_3) throw e_3.error; }
        }
    }
    function computeGroups(list) {
        if (list === void 0) { list = []; }
        var groupMap = list.reduce(function (acc, item) {
            var _a = item.category, category = _a === void 0 ? "other" : _a;
            if (!acc[category]) {
                acc[category] = {
                    category: category,
                    items: [item],
                };
            }
            else {
                acc[category].items.push(item);
            }
            return acc;
        }, {});
        return Object.keys(groupMap)
            .map(function (category) { return groupMap[category]; })
            .sort(function (a, b) {
            var groupA = groupPriorities.indexOf(a.category);
            var groupB = groupPriorities.indexOf(b.category);
            if (groupA < 0)
                groupA = Infinity;
            if (groupB < 0)
                groupB = Infinity;
            return groupA - groupB;
        });
    }
});
function SelectorDropDownListGroup(_a) {
    var itemClassName = _a.itemClassName, category = _a.category, items = _a.items, selected = _a.selected, focused = _a.focused, renderTitle = _a.renderTitle, renderItem = _a.renderItem, onSelect = _a.onSelect, onClickItem = _a.onClickItem;
    var title = renderTitle === null || renderTitle === void 0 ? void 0 : renderTitle(category, items);
    return (_jsxs("li", { className: classNames(styles["selector-drop-down-list-group"]), children: [!!title && _jsx("div", { className: styles["title"], children: title }), _jsx(Selector, { className: styles["list"], items: items, focused: focused, selected: selected, renderItem: function (item, isSelected, isFocused) { return (_jsx(SelectorDropDownListItem, { className: itemClassName, item: item, selected: isSelected, focused: isFocused, renderItem: renderItem })); }, onChange: onSelect, onClickItem: onClickItem })] }));
}
function SelectorDropDownListItem(_a) {
    var _b;
    var className = _a.className, item = _a.item, selected = _a.selected, focused = _a.focused, renderItem = _a.renderItem;
    return (_jsx("div", { className: classNames(styles["item"], className, (_b = {},
            _b[styles["is-focused"]] = focused || selected,
            _b)), children: renderItem === null || renderItem === void 0 ? void 0 : renderItem(item, selected, focused) }));
}
